// Hub — app shell.
//
// Gates on auth: shows the LoginView until useAuth confirms the user
// is signed in, then renders the topbar + section router + tweaks +
// user menu. The AuthInterstitial is gone — a mid-session 401 just
// flips useAuth back to logged-out, so the LoginView appears in place.

import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import "./styles/shell.css";

import { useAuth } from "./lib/use-auth";
import { useMe } from "./lib/use-me";
import { useTweaks } from "./lib/use-tweaks";
import { useAlarmsBridge } from "./lib/use-alarms-bridge";
import { warmCache } from "./lib/cache-warmup";
import { useLocalTimer } from "./lib/use-local-timer";
import { api } from "./lib/rpc";
import { sse } from "./lib/sse";
import { playKitchenAlarm } from "./lib/kitchen-alarm";

// Mount-only component that runs useAlarmsBridge inside the authed
// branch of App. Keeping it as a separate component (vs calling the
// hook directly in App) is what lets us gate the hook on auth without
// breaking the Rules of Hooks. Renders nothing.
function AlarmsBridgeMount() {
  useAlarmsBridge();
  return null;
}

// Mount-only component that fires the eager cache warmer once the
// adult shell is authed. Prefetches shopping/todos/recipes/pantry/
// calendar/mealplan list endpoints + walks all notes roots, so when
// the user wanders into a section they haven't visited yet (or loses
// network mid-session before opening it), the cache already has it.
// See cache-warmup.ts for what is and isn't included.
function CacheWarmupMount() {
  useEffect(() => {
    // Fire-and-forget. Module-scoped flag in warmCache() makes this
    // a no-op on subsequent mounts within the same session.
    warmCache();
  }, []);
  return null;
}

// Mount-only component that subscribes to local_timer:set SSE events
// and routes them to either window.VultureKitchen (inside the Kitchen
// Electron app) or a renderer-side fallback (regular browsers). See
// lib/use-local-timer.ts and src/local_timer_tools.mjs for both sides
// of this protocol.
function LocalTimerMount({ notify }: { notify: (msg: string) => void }) {
  useLocalTimer({
    onScheduled: (t) => {
      const minutes = Math.round(t.fireAtMs - Date.now()) / 1000 / 60;
      notify(
        minutes >= 1
          ? `${t.label} set · ${Math.round(minutes)} min`
          : `${t.label} set`,
      );
    },
    onFiredFallback: (t) => {
      notify(`⏰ ${t.label} done`);
      playKitchenAlarm();
    },
  });
  return null;
}

// KioskNavMount — subscribes to `kiosk:navigate` SSE events from the
// agent's navigate_kiosk tool and routes them through the existing
// setTab + FoodView deep-link props. Run-scoped so only the device
// the agent is talking to navigates.
function KioskNavMount({
  setTab,
  setFoodInitialMode,
  setFoodInitialRecipeId,
}: {
  setTab: (id: string) => void;
  setFoodInitialMode: (m: "shopping" | "pantry" | "recipes" | "meal" | null) => void;
  setFoodInitialRecipeId: (id: string | null) => void;
}) {
  useEffect(() => {
    return sse.on((ev: unknown) => {
      const e = ev as { type?: string; tab?: string; foodMode?: string; recipeId?: string };
      if (e?.type !== "kiosk:navigate") return;
      if (e.tab === "food" && e.foodMode) {
        setFoodInitialMode(e.foodMode as "shopping" | "pantry" | "recipes" | "meal");
      } else if (e.tab) {
        setFoodInitialMode(null);
      }
      if (e.recipeId) setFoodInitialRecipeId(String(e.recipeId));
      if (e.tab) setTab(e.tab);
    });
  }, [setTab, setFoodInitialMode, setFoodInitialRecipeId]);
  return null;
}

// KitchenAlarmRinger — subscribes to the Electron bridge's onTimerFired
// event (any timer scheduled via VultureKitchen.scheduleTimer) and rings
// the loud alarm. Local-timer fallback path rings via LocalTimerMount's
// onFiredFallback hook; this covers the kitchen-bridge path that walks
// past the renderer entirely.
function KitchenAlarmRinger({ notify }: { notify: (msg: string) => void }) {
  useEffect(() => {
    const bridge = (window as unknown as {
      VultureKitchen?: { onTimerFired?: (h: (p: { localId?: string; label?: string }) => void) => () => void };
    }).VultureKitchen;
    if (!bridge?.onTimerFired) return;
    return bridge.onTimerFired((payload) => {
      const label = payload?.label || "Timer";
      notify(`⏰ ${label} done`);
      playKitchenAlarm();
    });
  }, [notify]);
  return null;
}

// KitchenAlarmsBridge — when running on the kitchen kiosk (kioskProfile
// === "kitchen"), pipe server-side alarms (the cook-timer dock, the
// agent's set_alarm tool, settings/alarms.tsx) onto the Electron
// scheduler so the kiosk owns local firing + audible alarm. Without
// this hook, alarm rows exist on the server but no client rings on
// the kitchen (useAlarmsBridge no-ops without window.VultureAlarms).
function KitchenAlarmsBridge() {
  useEffect(() => {
    const bridge = (window as unknown as {
      VultureKitchen?: {
        scheduleTimer?: (json: string) => Promise<string> | string;
        cancelTimer?: (id: string) => Promise<string> | string;
      };
    }).VultureKitchen;
    if (!bridge?.scheduleTimer) return;
    interface AlarmRow { id: string; name: string; enabled: boolean; fireAt: number; atHHMM?: string | null }
    const computeNextFire = (a: AlarmRow): number | null => {
      const now = Date.now();
      if (Number.isFinite(a.fireAt) && a.fireAt > now) return a.fireAt;
      const m = a.atHHMM ? /^(\d{1,2}):(\d{2})$/.exec(a.atHHMM) : null;
      if (!m) return null;
      const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
      const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
      const next = new Date();
      next.setHours(h, min, 0, 0);
      if (next.getTime() <= now) next.setDate(next.getDate() + 1);
      return next.getTime();
    };
    const program = (a: AlarmRow) => {
      const fireAtMs = computeNextFire(a);
      if (fireAtMs === null) return;
      const seconds = Math.max(1, Math.round((fireAtMs - Date.now()) / 1000));
      try {
        void bridge.scheduleTimer!(JSON.stringify({
          localId: `alarm:${a.id}`,
          seconds,
          label: a.name || "Alarm",
          fireAtMs,
        }));
      } catch (err) { console.warn("[kitchen-alarms] schedule failed:", err); }
    };
    let cancelled = false;
    // Boot sync — schedule every active alarm.
    api.alarmsList()
      .then((alarms) => { if (!cancelled) for (const a of alarms) if (a.enabled) program(a as AlarmRow); })
      .catch((err) => console.warn("[kitchen-alarms] list failed:", err?.message));
    // Live updates.
    const unsub = sse.on((ev: unknown) => {
      const e = ev as { type?: string; alarm?: AlarmRow; id?: string };
      if (e?.type === "alarm:schedule" && e.alarm) program(e.alarm);
      else if (e?.type === "alarm:cancel" && typeof e.id === "string") {
        try { void bridge.cancelTimer?.(`alarm:${e.id}`); } catch { /* ignore */ }
      }
    });
    return () => { cancelled = true; unsub(); };
  }, []);
  return null;
}

// KioskExitHotZone — invisible 80x80 px touch target in the top-LEFT
// corner of the kiosk. Press-and-hold for 3 s to exit the kiosk shell
// (calls window.VultureKitchen.exit()). Pairs with the Ctrl+Shift+Q
// global shortcut in main.js for an admin-keyboard backup path.
//
// Left corner (not right) so the gesture can't fight the QC bubble's
// dismiss X (which lives in the top-right in kiosk mode at 24px from
// each edge with a 44x44 hit target). Kitchen kiosk strips the topbar-
// right entirely (see topbar.tsx) so the top-left is unused real estate.
function KioskExitHotZone({ notify }: { notify: (msg: string) => void }) {
  const timerRef = useRef<number | null>(null);
  const progressRef = useRef(0);
  const onPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    notify("Hold to exit kiosk…");
    progressRef.current = Date.now();
    timerRef.current = window.setTimeout(() => {
      const bridge = (window as unknown as { VultureKitchen?: { exit?: () => Promise<string> } }).VultureKitchen;
      if (bridge?.exit) void bridge.exit();
    }, 3000);
  };
  const onPointerUp = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: 80,
        height: 80,
        zIndex: 99999,
        background: "transparent",
        cursor: "default",
        userSelect: "none",
        WebkitUserSelect: "none",
        touchAction: "none",
        pointerEvents: "auto",
      }}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      onPointerCancel={onPointerUp}
      onContextMenu={(e) => e.preventDefault()}
      onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
      aria-label="Hold to exit kiosk"
    />
  );
}

// KitchenWakeMount — subscribes to the Electron kitchen-shell bridge
// (window.VultureKitchen.onWake) and surfaces every detection through
// the same notify() toast stack the rest of the app uses. Voice capture
// is a follow-up; this is the v1 "the IPC chain reached the renderer"
// signal so we can validate openWakeWord → pipe → Electron → SPA.
// No-op in non-Electron contexts (renders to null either way).
function KitchenWakeMount({ notify }: { notify: (msg: string) => void }) {
  useEffect(() => {
    const bridge = (window as unknown as {
      VultureKitchen?: { onWake?: (h: (p: { score: number | null; ts: number; debug?: boolean }) => void) => () => void };
    }).VultureKitchen;
    if (!bridge?.onWake) return;
    return bridge.onWake((payload) => {
      const score = typeof payload?.score === "number" ? payload.score.toFixed(2) : "?";
      notify(`🎙️ Heard you · ${score}`);
    });
  }, [notify]);
  return null;
}

// BrowserKitchenWakeMount — drives the wake detector inside the browser
// instead of relying on the Electron-bridge wake_service. Mounts a
// WakeMicLoop (16 kHz getUserMedia → 3-stage ONNX chain in
// onnxruntime-web), and on each "Kaw Kaw" fires the cached
// wake_acknowledge phrase via /api/tts so the kiosk audibly responds
// in <1 ms (pure cache hit; see scripts/tts-cache/templates.json).
//
// Runs in parallel with KitchenWakeMount above — both can fire while
// the bridge-side Python wake_service is being decom'd (wake-word
// Phase 4). When the bridge isn't available (web-only kitchen device,
// dev browser), this is the only wake source. When both fire on the
// same utterance, the user hears at most one ack — second arrival is
// dropped by the inFlight gate.
// Type-only import — the runtime side is dynamic-imported inside the
// effect so the ~26 MB onnxruntime-web wasm bundle isn't pulled into
// the main chunk for non-kiosk page loads.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type WakeMicLoop = import("./lib/wake-mic-loop").WakeMicLoop;

// Subscriber set shared between the shim VultureKitchen.onWake (which
// QuickChat / KitchenScreensaver subscribe to) and the browser wake
// loop (which fires INTO the shim once the ack audio finishes playing).
// Module-scoped so we install the shim exactly once even across HMR /
// remounts.
const _browserWakeSubs = new Set<() => void>();
function _installVultureKitchenShim() {
  const w = window as unknown as {
    VultureKitchen?: { onWake?: (h: () => void) => () => void };
  };
  // Don't clobber a real Electron-preload bridge — when both exist the
  // bridge handles screensaver+QC and we only play the ack. The check
  // is conservative: any pre-existing onWake means "leave it alone".
  if (w.VultureKitchen?.onWake) return false;
  w.VultureKitchen = {
    onWake: (h: () => void) => {
      _browserWakeSubs.add(h);
      return () => { _browserWakeSubs.delete(h); };
    },
  };
  return true;
}
function _fireBrowserWake() {
  for (const h of _browserWakeSubs) {
    try { h(); } catch (err) { console.warn("[browser-wake] sub threw:", err); }
  }
}

function BrowserKitchenWakeMount({ notify }: { notify: (msg: string) => void }) {
  useEffect(() => {
    // Bail on platforms without WebRTC mic (server-side render, very
    // old browsers, Electron preload phase). The kiosk profile is the
    // gate the caller already uses, so we don't double-check here.
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      console.warn("[browser-wake] navigator.mediaDevices unavailable — skipping");
      return;
    }

    // Install the shim so QuickChat/KitchenScreensaver's existing
    // bridge.onWake subscriptions get the browser-detected wake too.
    // shimInstalled === false means a real Electron bridge is already
    // present; in that case we still run the wake loop (so the cache
    // ack plays via the browser audio path), but we don't fire shim
    // subscribers — the bridge already fans the event out.
    const shimInstalled = _installVultureKitchenShim();

    let loop: WakeMicLoop | null = null;
    let canceled = false;
    let inFlight = false;
    let lastAckTs = 0;

    // Re-entrant lockout window. WakeDetector already has its own
    // cooldown, but a 1.5 s SPA-side gate guards against a same-frame
    // double-fire from (bridge wake + browser wake) on the same
    // utterance — neither path knows about the other.
    const ACK_LOCKOUT_MS = 1500;

    (async () => {
      try {
        const mod = await import("./lib/wake-mic-loop");
        if (canceled) return;
        loop = new mod.WakeMicLoop();
        loop.onWake(async ({ score, ts }) => {
          if (inFlight) return;
          if (ts - lastAckTs < ACK_LOCKOUT_MS) return;
          inFlight = true;
          lastAckTs = ts;
          notify(`🎙️ Heard you · ${score.toFixed(2)}`);
          let ackAudio: HTMLAudioElement | null = null;
          try {
            const tts = await import("./lib/wake-tts");
            ackAudio = await tts.playWakeAcknowledge();
          } catch (err) {
            console.warn("[browser-wake] ack playback failed:", err);
          }
          // Fire wake handlers AFTER the ack finishes — otherwise
          // QuickChat's wake handler opens the mic mid-ack and STT
          // would transcribe our own voice. Two fan-out paths:
          //   - shim subscribers (when we installed the shim — non-kiosk
          //     dev browser, no Electron bridge present)
          //   - native bridge re-broadcast via VultureMic.fireBrowserWake
          //     (chat-kitchen — the real VultureKitchen.onWake from
          //     preload then dispatches to QuickChat/screensaver)
          const fanOut = () => {
            inFlight = false;
            if (shimInstalled) _fireBrowserWake();
            try {
              const vm = (window as unknown as { VultureMic?: { fireBrowserWake?: (s: number, t: number) => Promise<unknown> } }).VultureMic;
              if (vm && typeof vm.fireBrowserWake === "function") {
                void vm.fireBrowserWake(score, ts);
              }
            } catch (err) {
              console.warn("[browser-wake] fireBrowserWake threw:", err);
            }
          };
          if (ackAudio) {
            ackAudio.addEventListener("ended", fanOut, { once: true });
            ackAudio.addEventListener("error", fanOut, { once: true });
            // Safety net: if playback hangs (audio backend stalled),
            // free the lockout after a generous max-ack window so a
            // single bad playback doesn't lock the kiosk permanently.
            setTimeout(() => { if (inFlight) fanOut(); }, 4000);
          } else {
            // No audio (cache miss + network down, or autoplay
            // blocked). Still fire subscribers so the wake event isn't
            // silently dropped.
            fanOut();
          }
        });
        await loop.start();
      } catch (err) {
        // Mic denied, ONNX failed to load, etc. Don't crash the kiosk
        // — the legacy bridge wake (KitchenWakeMount) may still work,
        // and the rest of the SPA needs to stay usable.
        console.warn("[browser-wake] start failed:", err);
      }
    })();

    return () => {
      canceled = true;
      void loop?.stop();
    };
  }, [notify]);
  return null;
}

import { Topbar, type Tab } from "./components/topbar";
import { MobileNav } from "./components/mobile-nav";
import { UserMenu } from "./components/user-menu";
import { TweaksPanel } from "./components/tweaks-panel";
import { KitchenScreensaver } from "./components/kitchen-screensaver";
import { QuickChat } from "./components/quick-chat";
import { Toast } from "./components/toast";
import { UpdateBanner } from "./components/update-banner";
import { BundleUpdatePill } from "./components/bundle-update-pill";
import { HouseholdSetupBanner } from "./components/household-setup-banner";
import { GlobalSearchPalette, type GlobalSearchPickHandler } from "./components/global-search";

import { Placeholder } from "./sections/placeholder";
import { ChatView } from "./sections/chat";
import { LoginView } from "./sections/login";
import { TodosView } from "./sections/todos";
import { ChoresView } from "./sections/chores";
import { FoodView } from "./sections/food";
import { NotesView } from "./sections/notes";
import { CalendarView } from "./sections/calendar";
import { TodayView } from "./sections/today";
import { StudioView } from "./sections/studio";
import { SettingsView } from "./sections/settings";
import { DownloadsView } from "./sections/downloads";
import { AdminView } from "./sections/admin";
import { FamilyView } from "./sections/family";
import { TotpModal } from "./sections/settings/totp-modal";
import { DeviceTokensModal } from "./sections/settings/device-tokens-modal";
import { SecretsModal } from "./sections/settings/secrets-modal";
import { ConfirmHost } from "./components/confirm";
import { KidApp } from "./kid-app";
import { KidLoginView } from "./sections/kid-login";

const TABS: Tab[] = [
  { id: "today",    label: "Today",    icon: "home" },
  { id: "chat",     label: "Chat",     icon: "chat" },
  { id: "notes",    label: "Notes",    icon: "notes" },
  { id: "food",     label: "Food",     icon: "food" },
  { id: "todo",     label: "To-do",    icon: "todo" },
  { id: "chores",   label: "Chores",   icon: "star" },
  { id: "calendar", label: "Calendar", icon: "calendar" },
  { id: "images",   label: "Studio",   icon: "image" },
];

// Kiosk profiles — the SPA can render itself in a stripped-down kiosk
// mode keyed off `?kiosk=<profile>` in the URL. The kitchen device
// (counter-mounted Surface Pro 4 with wake-word, see
// docs/superpowers/specs/2026-05-31-kitchen-device-design.md) opens
// the SPA with `?kiosk=kitchen` and gets Today / Food / Calendar
// only — no chat scrollback, no admin, no notes, no studio. Other
// profiles can land later without touching this list's call-sites.
const KIOSK_TABS: Record<string, string[]> = {
  kitchen: ["today", "food", "calendar"],
};

function readKioskProfile(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = new URL(window.location.href).searchParams.get("kiosk");
    return v && v in KIOSK_TABS ? v : null;
  } catch { return null; }
}

const TAGLINES: Record<string, string> = {
  today:    "A gentle landing on what's ahead.",
  chat:     "Talk it through.",
  notes:    "Your scratchpad and your archive.",
  food:     "Shopping, recipes, the week.",
  todo:     "A few things, when you're ready.",
  chores:   "Who's doing what.",
  calendar: "Where the week sits.",
  images:   "Imagine and look back.",
};

export function App() {
  const auth = useAuth();
  // Pass auth.info?.scope as the session key so whoami re-fetches when
  // the session flips kid-device → kid after a PIN login (auth.status
  // stays "logged-in" across that flip, so it alone wouldn't re-trigger).
  const me = useMe(auth.status, auth.info?.scope);
  const [tweaks, setTweak] = useTweaks();
  // Kiosk profile is captured once on mount — it's derived from the
  // URL we were launched with and doesn't change at runtime. Used by
  // the tab filter, the user menu (hidden in kiosk), and the kitchen-
  // specific bits (listening indicator). Returns null on a normal load.
  const kioskProfile = useMemo(() => readKioskProfile(), []);
  // Mirror the kiosk profile onto the document body so global CSS can
  // flip layouts (e.g. force the mobile bottom-nav at desktop viewports
  // for the kitchen kiosk).
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (kioskProfile) document.body.dataset.kiosk = kioskProfile;
    else delete document.body.dataset.kiosk;
    return () => { delete document.body.dataset.kiosk; };
  }, [kioskProfile]);
  const visibleTabs = useMemo(() => {
    if (!kioskProfile) return TABS;
    const allowed = new Set(KIOSK_TABS[kioskProfile] || []);
    return TABS.filter((t) => allowed.has(t.id));
  }, [kioskProfile]);
  const [tab, setTab] = useState(() => {
    if (kioskProfile) {
      const allowed = KIOSK_TABS[kioskProfile] || [];
      return allowed[0] || "today";
    }
    return "today";
  });
  const [toast, setToast] = useState<string | null>(null);
  const [toastAction, setToastAction] = useState<{ label: string; onClick: () => void } | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [familyOpen, setFamilyOpen] = useState(false);
  const [downloadsOpen, setDownloadsOpen] = useState(false);
  const [totpOpen, setTotpOpen] = useState(false);
  const [deviceTokensOpen, setDeviceTokensOpen] = useState(false);
  const [secretsOpen, setSecretsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Food-sub-tab initial-mode hint. Producers: the global ⌘K search
  // palette (handleSearchPick), the notifications bell, and the hash
  // deep-link router below. Consumed by <FoodView initialMode/> on
  // mount; FoodView nulls it back via onConsumeInitialMode so a
  // later natural visit doesn't re-route.
  const [foodInitialMode, setFoodInitialMode] = useState<"shopping" | "pantry" | "recipes" | "meal" | null>(null);
  // Voice-nav deep link: the `navigate_kiosk` tool can request a
  // specific recipe id to open in the food section. Stashed here so
  // setTab + FoodView mount can both pick it up cleanly.
  const [foodInitialRecipeId, setFoodInitialRecipeId] = useState<string | null>(null);
  // Studio gallery jump — when the user picks "Gallery" from the user
  // menu we switch to the Studio tab AND ask the StudioView to start
  // on the Gallery pane. After consume the hint clears.
  const [studioStartPane, setStudioStartPane] = useState<"imagine" | "gallery" | null>(null);
  // Cross-section bridge: Today's Quick chat drops a string here, then
  // flips to the chat tab; ChatView consumes it on mount and clears.
  const [pendingChatMessage, setPendingChatMessage] = useState<string | null>(null);

  // notify(msg) — plain toast, 1.8s
  // notify(msg, { action: { label, onClick } }) — actionable toast,
  // 5s window so the user has time to click the action. Clicking the
  // action calls onClick AND clears the toast immediately.
  const notify = useCallback((msg: string, opts?: { action?: { label: string; onClick: () => void } }) => {
    if (toastTimerRef.current != null) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(msg);
    if (opts?.action) {
      const wrapped = {
        label: opts.action.label,
        onClick: () => {
          opts.action!.onClick();
          setToast(null);
          setToastAction(null);
        },
      };
      setToastAction(wrapped);
      toastTimerRef.current = window.setTimeout(() => {
        setToast(null);
        setToastAction(null);
      }, 5000);
    } else {
      setToastAction(null);
      toastTimerRef.current = window.setTimeout(() => setToast(null), 1800);
    }
  }, []);

  const _ctx = useMemo(
    () => ({ setTab, notify, bubble: tweaks.bubble, sidebarDense: tweaks.sidebarDense }),
    [setTab, notify, tweaks.bubble, tweaks.sidebarDense],
  );

  // ⌘K / Ctrl+K opens the global search palette from anywhere in the
  // app. preventDefault on the keydown so the browser's "search the
  // page" affordance doesn't compete (Safari binds the same combo).
  // Skipped on input/textarea/contenteditable focus so typing into the
  // chat composer doesn't pop the palette mid-message — but the chat
  // sidebar's own search input no longer claims this combo (the
  // duplicate ⌘K hint was removed in this MR).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        const isEditable = tag === "INPUT" || tag === "TEXTAREA" || (t as any)?.isContentEditable;
        if (isEditable) return;
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Notification deep-link router. The Android wrapper sets
  // window.location.hash = "#notif=pantry" when the user taps a
  // server-pushed OS notification (AlarmsBridge.notifyNow). We read
  // that on mount AND on hashchange (the wrapper fires it from
  // onNewIntent for already-running app) and route to the
  // corresponding section, then clear the hash so a manual reload
  // doesn't re-route.
  useEffect(() => {
    const route = () => {
      const m = window.location.hash.match(/^#notif=([\w-]+)/);
      if (!m) return;
      const target = m[1];
      // Clear immediately so the routing is one-shot.
      try { history.replaceState(null, "", window.location.pathname + window.location.search); }
      catch { window.location.hash = ""; }
      if (target === "pantry") {
        setFoodInitialMode("pantry");
        setTab("food");
      } else if (target === "chores") {
        setTab("chores");
      } else if (target === "calendar") {
        setTab("calendar");
      } else if (target === "family" || target === "kidchat") {
        // kidchat-flagged notifications surface in the Family panel's
        // kid-chat review section, so both kinds land on Family.
        setFamilyOpen(true);
      } else if (target === "notes") {
        setTab("notes");
      } else if (target === "todo") {
        setTab("todo");
      } else if (target === "chat") {
        setTab("chat");
      }
    };
    route();
    window.addEventListener("hashchange", route);
    return () => window.removeEventListener("hashchange", route);
  }, []);

  // Route a global-search pick to the right tab. The destination
  // section reads its initial sub-state from props we already plumb
  // through (food's initialMode, etc — see below), so a "Recipes:
  // Risotto" pick lands the user on the Recipes pane rather than
  // the default Shopping pane.
  const handleSearchPick: GlobalSearchPickHandler = useCallback((section, focus) => {
    if (section === "notes") setTab("notes");
    else if (section === "todo") setTab("todo");
    else if (section === "food") {
      setTab("food");
      if (focus.kind === "recipe") setFoodInitialMode("recipes");
      else if (focus.kind === "shopping") setFoodInitialMode("shopping");
      else if (focus.kind === "pantry") setFoodInitialMode("pantry");
    }
  }, []);

  // Pre-auth state — first probe call hasn't finished yet. Render
  // nothing rather than flashing the login form for users who are
  // already authenticated. Tweaks panel still mounts (so the user can
  // change theme before sign-in if they want), but no chrome.
  if (auth.status === "unknown") {
    return (
      <div style={{ height: "100vh", display: "grid", placeItems: "center" }}>
        <p
          style={{
            fontFamily: "var(--f-mono)",
            color: "var(--text-dim)",
            fontSize: 11,
            letterSpacing: "0.08em",
          }}
        >
          loading…
        </p>
      </div>
    );
  }

  if (auth.status === "logged-out") {
    return (
      <>
        <LoginView auth={auth} />
        <BundleUpdatePill />
        <TweaksPanel tweaks={tweaks} setTweak={setTweak} />
      </>
    );
  }

  // Kid scope: PIN-issued sessions render a completely separate shell
  // (no chat / notes / food / studio tabs, no user menu with admin
  // options, no global search). Whoami hasn't returned yet means we
  // hold the loading state — defaulting to the adult shell would briefly
  // flash adult tabs to a kid.
  if (me === null) {
    return (
      <div style={{ height: "100vh", display: "grid", placeItems: "center" }}>
        <p style={{ fontFamily: "var(--f-mono)", color: "var(--text-dim)", fontSize: 11, letterSpacing: "0.08em" }}>
          loading…
        </p>
      </div>
    );
  }
  // Shared family-tablet ("kid-device") session: the device is signed in
  // to the household's kids account, but no specific kid is selected yet.
  // Render the PIN picker — same component as the chat2 kid-login screen,
  // but here it's reached from an authenticated device session. After a
  // kid taps their face + PIN, the session flips to scope=kid (via
  // /api/kid-login) and auth.refresh() re-renders into KidApp below.
  // "Back" signs the device out (parent reconfigures / hands to an adult).
  if (me.scope === "kid-device") {
    return (
      <>
        <KidLoginView auth={auth} onBack={() => auth.logout()} />
        <BundleUpdatePill />
      </>
    );
  }
  if (me.scope === "kid") {
    return (
      <>
        <KidApp />
        <BundleUpdatePill />
      </>
    );
  }

  return (
    <div className="app">
      {/* AlarmsBridgeMount lives inside the authed branch so the hook
       *  inside it only runs when alarms.list will actually return data
       *  (the RPC needs an auth cookie). On regular browsers (no
       *  window.VultureAlarms) the hook is a complete no-op, so this
       *  component is invisible on desktop. */}
      <AlarmsBridgeMount />
      <CacheWarmupMount />
      <LocalTimerMount notify={notify} />
      <KitchenWakeMount notify={notify} />
      <KitchenAlarmRinger notify={notify} />
      <KioskNavMount
        setTab={setTab}
        setFoodInitialMode={setFoodInitialMode}
        setFoodInitialRecipeId={setFoodInitialRecipeId}
      />
      {kioskProfile === "kitchen" && <KitchenAlarmsBridge />}
      {kioskProfile === "kitchen" && <KioskExitHotZone notify={notify} />}
      {kioskProfile === "kitchen" && <BrowserKitchenWakeMount notify={notify} />}
      <KitchenScreensaver kioskProfile={kioskProfile} />
      <Topbar
        tabs={visibleTabs}
        tab={tab}
        setTab={setTab}
        me={me}
        kioskProfile={kioskProfile}
        onAvatar={() => setUserMenuOpen((v) => !v)}
        onSearch={() => setSearchOpen(true)}
        onOpenPantry={() => {
          setFoodInitialMode("pantry");
          setTab("food");
        }}
        onOpenChores={() => setTab("chores")}
        onOpenCalendar={() => setTab("calendar")}
        onOpenFamily={() => setFamilyOpen(true)}
      />
      <GlobalSearchPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onPick={handleSearchPick}
      />
      {/* User menu rendered OUTSIDE the topbar (was previously a child of
       *  topbar-right). The topbar has `backdrop-filter` which, per CSS
       *  spec, makes it the containing block for `position: fixed`
       *  descendants. Mobile menu uses `bottom: 80px` to float above the
       *  bottom nav — when anchored to the 50px-tall topbar instead of
       *  the viewport, that lands the menu OFF-SCREEN (top ≈ -509px).
       *  Hoisting the menu out of topbar restores the viewport as the
       *  containing block. Desktop CSS adjusted to use fixed coords
       *  (top: 56px right: 20px) to compensate for no longer being
       *  topbar-anchored. */}
      {me && (
        <UserMenu
          open={userMenuOpen}
          onClose={() => setUserMenuOpen(false)}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenAdmin={() => setAdminOpen(true)}
          onOpenFamily={() => setFamilyOpen(true)}
          onSignOut={() => auth.logout()}
          me={me}
        />
      )}

      <UpdateBanner notify={notify} />
      <BundleUpdatePill />
      <HouseholdSetupBanner notify={notify} />

      <main className="main">
        <div className="view" key={tab}>
          {tab === "chat" ? (
            <ChatView
              me={me}
              tweaks={tweaks}
              notify={notify}
              pendingMessage={pendingChatMessage}
              onPendingConsumed={() => setPendingChatMessage(null)}
            />
          ) : tab === "todo" ? (
            <TodosView notify={notify} />
          ) : tab === "chores" ? (
            <ChoresView notify={notify} />
          ) : tab === "food" ? (
            <FoodView
              notify={notify}
              initialMode={foodInitialMode}
              onConsumeInitialMode={() => setFoodInitialMode(null)}
              initialRecipeId={foodInitialRecipeId}
              onConsumeInitialRecipeId={() => setFoodInitialRecipeId(null)}
            />
          ) : tab === "notes" ? (
            <NotesView notify={notify} />
          ) : tab === "calendar" ? (
            <CalendarView notify={notify} meId={me?.username || null} />
          ) : tab === "today" ? (
            <TodayView
              me={me}
              setTab={setTab}
              setFoodInitialMode={(mode) => { setFoodInitialMode(mode); }}
              setFoodInitialRecipeId={(id) => { setFoodInitialRecipeId(id); }}
              notify={notify}
              setPendingChatMessage={setPendingChatMessage}
              kioskProfile={kioskProfile}
            />
          ) : tab === "images" ? (
            <StudioView
              notify={notify}
              startPane={studioStartPane}
              onConsumeStartPane={() => setStudioStartPane(null)}
            />
          ) : (
            <Placeholder
              name={TABS.find((t) => t.id === tab)?.label ?? "Section"}
              tagline={TAGLINES[tab] ?? ""}
            />
          )}
        </div>
      </main>

      <MobileNav tabs={visibleTabs} tab={tab} setTab={setTab} />

      <TweaksPanel tweaks={tweaks} setTweak={setTweak} />

      {/* Mobile-only voice-assistant FAB. Lives at the same bottom-right
        * slot as the TweaksPanel FAB; CSS hides one or the other based on
        * viewport (tweaks FAB stays for desktop where the chat tab is
        * always visible in the sidebar). Hidden when tab === "chat" so
        * the composer's mic isn't doubled up. */}
      <QuickChat currentTab={tab} kioskProfile={kioskProfile} />

      <SettingsView
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        me={me}
        tweaks={tweaks}
        setTweak={setTweak}
        notify={notify}
        onOpenTotp={() => setTotpOpen(true)}
        onOpenDeviceTokens={() => setDeviceTokensOpen(true)}
        onOpenSecrets={() => setSecretsOpen(true)}
        onOpenDownloads={() => setDownloadsOpen(true)}
      />

      <TotpModal         open={totpOpen}         onClose={() => setTotpOpen(false)}         notify={notify} />
      <DeviceTokensModal open={deviceTokensOpen} onClose={() => setDeviceTokensOpen(false)} notify={notify} />
      <SecretsModal      open={secretsOpen}      onClose={() => setSecretsOpen(false)}      notify={notify} />
      <DownloadsView     open={downloadsOpen}    onClose={() => setDownloadsOpen(false)} />

      <AdminView
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        me={me}
        notify={notify}
      />

      <FamilyView
        open={familyOpen}
        onClose={() => setFamilyOpen(false)}
        me={me}
        notify={notify}
      />

      <Toast msg={toast} action={toastAction || undefined} />

      <ConfirmHost />
    </div>
  );
}
